import React, { useState, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { createTemplate } from '@model/template';
import { t } from 'i18next';
import {IconClose} from "@/Icon";
import {imgPathPerfix, isDarkMode} from "@/utils";
import {createPortal} from "react-dom";

interface TemplateDialogState {
  isOpen: boolean;
  content: string;
}

interface TemplateResult {
  title: string;
  content: string;
}

class TemplateManager {
  private static instance: TemplateManager;
  private setState: React.Dispatch<React.SetStateAction<TemplateDialogState>> | null = null;
  private resolvePromise: ((value: TemplateResult | null) => void) | null = null;

  static getInstance(): TemplateManager {
    if (!TemplateManager.instance) {
      TemplateManager.instance = new TemplateManager();
    }
    return TemplateManager.instance;
  }

  init(setState: React.Dispatch<React.SetStateAction<TemplateDialogState>>) {
    this.setState = setState;
  }

  /**
   * 打开模板创建弹窗
   * @param content 选中的内容 (HTML字符串)
   */
  open(content: string = ''): Promise<TemplateResult | null> {
    return new Promise((resolve) => {
      if (!this.setState) {
        resolve(null);
        return;
      }

      this.resolvePromise = resolve;

      this.setState({
        isOpen: true,
        content,
      });
    });
  }

  close(result: TemplateResult | null = null) {
    if (this.resolvePromise) {
      this.resolvePromise(result);
      this.resolvePromise = null;
    }
    if (this.setState) {
      this.setState({ isOpen: false, content: '' });
    }
  }
}

// 弹窗组件
const TemplateDialog: React.FC<{
  content: string;
  onSave: (title: string, content: string) => void;
  onCancel: () => void;
}> = ({ content, onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // 自动聚焦输入框 - 使用 setTimeout 确保 DOM 已完全渲染
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSave = () => {
    if (title.trim()) {
      onSave(title.trim(), content);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && title.trim()) {
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--background-color-white)',
          borderRadius: '8px',
          width: '480px',
          padding: '16px 16px',
          boxShadow: 'var(--dialog-box-shadow)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
          <div className={'dialog-title flex-row-vertical-center'}>
              <span>{t('Save template')}</span>
              <div className={'flex-center cursor-pointer'} style={{marginLeft: 'auto',width:'24px',height:'24px'}} onClick={(e)=>{
                  onCancel();
              }}>
                  <img className='img-mini-small-10 cursor-pointer'
                       src={imgPathPerfix() + (isDarkMode() ? "close_white_1.png" : "close_black.png")}/>
              </div>

          </div>

          <div className={'line'} style={{margin: '12px -16px'}}></div>

          {/* 输入框 */}
          <input
              ref={inputRef}
              type="text"
          placeholder={t("Template name")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          className={'input-border mt-0'}
          style={{
              marginTop:'6px',
            width: '100%',
            padding: '8px 12px',
            borderRadius: '6px',
            outline: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
          // onFocus={(e) => {
          //   e.target.style.borderColor = '#4096ff';
          //   e.target.style.boxShadow = '0 0 0 2px rgba(5, 145, 255, 0.1)';
          // }}
          // onBlur={(e) => {
          //   e.target.style.borderColor = '#d9d9d9';
          //   e.target.style.boxShadow = 'none';
          // }}
        />

        {/* 按钮区域 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            marginTop: '32px',
          }}
        >
          <div
            onClick={onCancel}
            className={'button-border-gray-style font-size-14 font-family-medium ml-12 flex-center'}
            style={{
                minWidth: '164px', height: '38px',lineHeight:'38px', display:'block',textAlign:'center',
                borderRadius:'8px'
            }}>
              {t('Cancel')}
          </div>
          <div
            onClick={handleSave}
            className={'button-solid-style font-size-14 font-family-medium ml-12 flex-center'}
            style={{
                minWidth: '164px', height: '38px',lineHeight:'38px', display:'block',textAlign:'center'
            }}>
              {t('Save')}
          </div>
        </div>
      </div>
    </div>
  );
};

// Provider 组件
const TemplateDialogProvider: React.FC = () => {
  const [state, setState] = useState<TemplateDialogState>({
    isOpen: false,
    content: '',
  });

  useEffect(() => {
    const manager = TemplateManager.getInstance();
    manager.init(setState);
  }, []);

  const handleSave = useCallback(async (title: string, content: string) => {
    try {
      // 保存模板到数据库
      await createTemplate(title, content);
      message.success(t('Template added'));
      
      const manager = TemplateManager.getInstance();
      manager.close({ title, content });
    } catch (error) {
      console.error('Failed to save template:', error);
      message.error('Failed to save template');
    }
  }, []);

  const handleCancel = useCallback(() => {
    const manager = TemplateManager.getInstance();
    manager.close(null);
  }, []);

  if (!state.isOpen) return null;

  return createPortal(
    <TemplateDialog 
      content={state.content}
      onSave={handleSave} 
      onCancel={handleCancel} 
    />
  ,document.body);
};

// 导出单例实例和组件
export const Template = TemplateManager.getInstance();
export default TemplateDialogProvider;
